import { Link } from "react-router-dom";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import { ArrowRight } from "lucide-react";

export default function SolarCellVsPanel() {
  return (
    <div className="pb-28 lg:pb-0">
      <div className="max-w-3xl mx-auto px-4 py-12 lg:py-20">
        <nav className="text-sm font-mono text-muted-foreground mb-6">
          <Link to="/" className="hover:text-accent">Home</Link>
          <span className="mx-2">/</span>
          <span>Solar Cell vs Solar Panel</span>
        </nav>

        <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-4">
          Solar Cell vs Solar Panel: What's the Difference?
        </h1>
        <p className="text-lg text-muted-foreground mb-8">
          Understanding the difference between a solar cell and a solar panel is key to evaluating 
          efficiency claims and choosing the right product.
        </p>

        <AffiliateDisclosure />

        <div className="prose prose-lg max-w-none space-y-6 text-muted-foreground">
          <h2 className="font-heading font-bold text-2xl text-foreground">What Is a Solar Cell?</h2>
          <p>
            A solar cell (also called a photovoltaic cell) is the individual semiconductor unit that converts 
            sunlight directly into electricity through the photovoltaic effect. A single solar cell typically 
            produces 0.5–0.6 volts of electricity.
          </p>
          <p>
            Modern solar cells are usually made from silicon and come in several types: monocrystalline 
            (single crystal), polycrystalline (multiple crystals), and thin-film (amorphous silicon or other materials).
          </p>

          <h2 className="font-heading font-bold text-2xl text-foreground">What Is a Solar Panel?</h2>
          <p>
            A solar panel (or solar module) is a collection of solar cells wired together and enclosed in a 
            protective frame with a glass cover. A typical residential solar panel contains 60–72 individual cells. 
            The panel provides a usable voltage (typically 18–40V) for charging batteries or feeding an inverter.
          </p>

          <h2 className="font-heading font-bold text-2xl text-foreground">Why Panel Efficiency Is Lower Than Cell Efficiency</h2>
          <p>
            You'll often see news about solar cells achieving 25%, 30%, or even 47% efficiency in the lab. 
            But when those cells are assembled into panels, overall efficiency drops due to:
          </p>
          <ul className="space-y-2">
            <li><strong>Cell spacing:</strong> Gaps between cells don't produce electricity</li>
            <li><strong>Wiring losses:</strong> Electrical resistance in interconnections</li>
            <li><strong>Frame and glass:</strong> Some light is reflected or absorbed before reaching cells</li>
            <li><strong>Temperature effects:</strong> Panels operate hotter than lab conditions</li>
            <li><strong>Mismatch losses:</strong> Slight variations between cells reduce total output</li>
          </ul>

          <h2 className="font-heading font-bold text-2xl text-foreground">What This Means for Buyers</h2>
          <p>
            When shopping for solar panels, focus on panel-level efficiency, not cell-level efficiency. 
            A panel rated at 22% efficiency is excellent for consumer use, even though the individual cells 
            inside might be rated at 24% or higher.
          </p>
          <p>
            Also look at total wattage output, not just efficiency percentage. A 400W panel with 21% efficiency 
            may produce more power than a 200W panel with 23% efficiency — it just uses more surface area to do so.
          </p>

          <div className="bg-accent/10 border-l-4 border-accent rounded-r-xl p-6 not-prose">
            <p className="font-medium text-foreground">
              <strong>Key takeaway:</strong> The highest efficiency solar cell in a lab setting is interesting science, 
              but what matters for your purchase is the panel-level efficiency, total wattage, and real-world performance.
            </p>
          </div>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row gap-4">
          <Link
            to="/best-high-efficiency-solar-panels"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent text-foreground font-mono font-bold text-sm rounded-lg hover:bg-accent/90 min-h-[48px]"
          >
            Compare High-Efficiency Panels <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/monocrystalline-vs-polycrystalline-solar-panels"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-border text-sm font-medium rounded-lg hover:bg-secondary min-h-[48px]"
          >
            Mono vs Poly Guide
          </Link>
        </div>
      </div>
    </div>
  );
}